// app/src/lib/scale/market.js — client wrapper for the public /v1/market route.
//
// The endpoint is unauthenticated (Census data is public-domain), so this call
// works the same whether or not the user is signed in — but we forward the
// access token if available, purely so server-side rate limits can be partly
// keyed off the verified Cognito sub later if abuse appears.

import { getConfig } from '../config.js';
import { getAccessToken } from '../auth/cognitoClient.js';

/**
 * @param {{ zip:string, naics?:string, sizeClass?:string }} q
 * @returns {Promise<object>} parsed market payload (see CENSUS_BENCHMARKING_SPEC §4.2)
 */
export async function fetchMarket({ zip, naics, sizeClass } = {}) {
  if (!/^\d{5}$/.test(String(zip || ''))) {
    const e = new Error('zip must be 5 digits'); e.code = 'bad_zip'; throw e;
  }
  const cfg = getConfig();
  const qs = new URLSearchParams({ zip });
  if (naics) qs.set('naics', naics);
  if (sizeClass) qs.set('sizeClass', sizeClass);
  const at = await getAccessToken().catch(() => null);
  const res = await fetch(`${cfg.apiBaseUrl}/v1/market?${qs}`, {
    headers: at ? { authorization: `Bearer ${at}` } : {},
  });
  if (res.status === 404) {
    const body = await res.json().catch(() => ({}));
    const e = new Error(body.error || 'ZIP_NOT_BENCHMARKABLE');
    e.code = 'not_benchmarkable';
    throw e;
  }
  if (!res.ok) {
    const e = new Error(`market ${res.status}`); e.status = res.status; throw e;
  }
  return res.json();
}

/**
 * Pure comparison helper: how does the operator's annualized payroll sit
 * against the same-size peer cohort's average? Reads from a `cbp` block
 * shaped like `payload.combined.cbp` or `payload.zips[i].cbp`, so callers
 * can compare against the trade-area aggregate OR a single ZIP.
 *
 * @param {object} cbpBlock — { peerCohort: { avgPayrollPerEstablishmentUsd, ... } } | null
 * @param {number} userAnnualPayrollUsd — Kanjo-derived TTM payroll in dollars
 * @returns {{ peerAvgUsd:number, deltaUsd:number, deltaPct:number, direction:'below'|'above'|'on' } | null}
 */
export function peerPayrollComparison(cbpBlock, userAnnualPayrollUsd) {
  const peerAvg = cbpBlock?.peerCohort?.avgPayrollPerEstablishmentUsd;
  if (!Number.isFinite(peerAvg) || peerAvg <= 0) return null;
  if (!Number.isFinite(userAnnualPayrollUsd) || userAnnualPayrollUsd <= 0) {
    return { peerAvgUsd: peerAvg, deltaUsd: null, deltaPct: null, direction: null };
  }
  const deltaUsd = userAnnualPayrollUsd - peerAvg;
  const deltaPct = (deltaUsd / peerAvg) * 100;
  const direction = Math.abs(deltaPct) < 1 ? 'on' : (deltaPct < 0 ? 'below' : 'above');
  return { peerAvgUsd: peerAvg, deltaUsd, deltaPct, direction };
}
